/**
 * ScenarioPicker — the chosen scenario as a control: icon, name, chevron, and
 * a menu of the workshop's scenarios beneath. Replaces both the plain label
 * and the "back" that the v2 header lost with the Skenaariot switch.
 *
 * Not a native <select>: those open in the OS's own style. The menu is the
 * same dark surface the map's mode bar uses. Closes on outside click, Escape,
 * or choosing.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ScenarioIcon from './ScenarioIcon';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Root = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;
const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  font: inherit;
  font-size: 0.8rem;
  white-space: nowrap;
  cursor: pointer;
  &:hover, &[aria-expanded="true"] { background: rgba(255, 255, 255, 0.08); color: #fff; }
  &:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.5); outline-offset: 2px; }
`;
const Chevron = styled.span`
  width: 7px; height: 7px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: translateY(-2px) rotate(45deg);
  opacity: 0.75;
  transition: transform 0.15s ease;
  ${(p) => p.$open && 'transform: translateY(2px) rotate(225deg);'}
`;
const Menu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 60;
  min-width: 200px;
  padding: 6px;
  border-radius: 12px;
  background: #14141f;
  border: 1px solid rgba(255, 255, 255, 0.13);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  gap: 1px;
`;
const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: none;
  background: ${(p) => (p.$active ? 'rgba(255, 255, 255, 0.08)' : 'transparent')};
  color: #fff;
  font-family: inherit;
  font-size: 0.86rem;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  text-align: left;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.1); }
  &:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.5); outline-offset: -2px; }
`;

export default function ScenarioPicker({ scenarios = [], value = '', onChange = () => {}, title }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = scenarios.find((s) => s.id === value) || null;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // Capture phase: d3-zoom on the map's svg stops mousedown from
    // propagating, so a bubbling listener never hears a click on the map.
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown, true); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!scenarios.length) return null;

  return (
    <Root ref={ref}>
      <Trigger type="button" aria-haspopup="listbox" aria-expanded={open} title={title ?? t('impactMapV2.scenarioPicker.change')} onClick={() => setOpen((v) => !v)}>
        {current && <ScenarioIcon title={current.title} icon={current.icon} />}
        {current ? current.title : t('impactMapV2.scenarioPicker.pick')}
        <Chevron $open={open} />
      </Trigger>
      {open && (
        <Menu role="listbox" aria-label={t('impactMapV2.scenarioPicker.listLabel')}>
          {scenarios.map((sc) => (
            <Item
              key={sc.id}
              type="button"
              role="option"
              aria-selected={sc.id === value}
              $active={sc.id === value}
              onClick={() => { setOpen(false); if (sc.id !== value) onChange(sc.id); }}
            >
              <ScenarioIcon title={sc.title} icon={sc.icon} />
              {sc.title}
            </Item>
          ))}
        </Menu>
      )}
    </Root>
  );
}
